import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, ChevronDown, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const serviceLinks = [
  { to: "/services#residential", label: "Residential HVAC" },
  { to: "/services#commercial", label: "Commercial HVAC" },
  { to: "/services#installation", label: "System Installation" },
  { to: "/services#maintenance", label: "Maintenance Plans" },
  { to: "/services#emergency", label: "Emergency Repairs" },
];

const navLinkClass = ({ isActive }) =>
  `font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
    isActive ? "text-ember" : "text-slate-200 hover:text-white"
  }`;

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="main-header"
      className="sticky top-0 z-50 bg-navy/95 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-24 flex items-center justify-between gap-6">
        <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
          <img
            src="/logo.png"
            alt="McCann and Sons HVAC Service — Residential and Commercial"
            className="h-20 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
          <NavLink to="/" end className={navLinkClass} data-testid="nav-home">
            Home
          </NavLink>
          <NavLink to="/about" className={navLinkClass} data-testid="nav-about">
            About Us
          </NavLink>
          <div className="relative group">
            <NavLink
              to="/services"
              className={navLinkClass}
              data-testid="nav-services"
            >
              <span className="flex items-center gap-1">
                Services <ChevronDown className="h-3.5 w-3.5" />
              </span>
            </NavLink>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="bg-navy-deep border border-white/10 rounded-md shadow-2xl py-2 w-64">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    data-testid={`nav-dropdown-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block px-5 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <NavLink to="/contact" className={navLinkClass} data-testid="nav-contact">
            Contact
          </NavLink>
          <a
            href="https://blog.jcmandsonshvac.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-blog"
            className="font-mono text-xs uppercase tracking-[0.2em] text-slate-200 hover:text-white transition-colors flex items-center gap-1"
          >
            Blog <ExternalLink className="h-3 w-3" />
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="header-call-quote-btn"
            href="tel:3183122112"
            className="hidden sm:inline-flex items-center gap-2 bg-ember hover:bg-ember-dark text-navy-deep font-bold text-sm px-5 py-3 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
          >
            <Phone className="h-4 w-4" />
            Call Now / Get a Quote
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden text-white p-2 -mr-2"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-navy-deep border-white/10 w-80">
              <nav className="flex flex-col gap-1 mt-10" aria-label="Mobile navigation">
                {[
                  { to: "/", label: "Home", tid: "mobile-nav-home" },
                  { to: "/about", label: "About Us", tid: "mobile-nav-about" },
                  { to: "/services", label: "Services", tid: "mobile-nav-services" },
                  { to: "/contact", label: "Contact", tid: "mobile-nav-contact" },
                ].map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={l.tid}
                    onClick={() => setOpen(false)}
                    className="font-display text-3xl font-bold uppercase tracking-wide text-white hover:text-ember transition-colors py-2"
                  >
                    {l.label}
                  </Link>
                ))}
                <div className="mt-4 pl-1 flex flex-col gap-2 border-l-2 border-ember/40">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setOpen(false)}
                      className="text-sm text-slate-300 hover:text-white transition-colors px-3 py-1"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
                <a
                  href="https://blog.jcmandsonshvac.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-blog"
                  className="font-display text-3xl font-bold uppercase tracking-wide text-white hover:text-ember transition-colors py-2 flex items-center gap-2"
                >
                  Blog <ExternalLink className="h-5 w-5" />
                </a>
                <a
                  data-testid="mobile-call-btn"
                  href="tel:3183122112"
                  className="mt-6 inline-flex items-center justify-center gap-2 bg-ember text-navy-deep font-bold px-5 py-4 rounded-sm"
                >
                  <Phone className="h-4 w-4" />
                  Call (318) 312-2112
                </a>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
